import { initializeApp, getApps } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, signInWithCustomToken, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app as mainApp } from '../firebase';

// The minted reader identity (must match the server default / WEBSITE_READER_UID
// in functions/src/functions/callable/website-token.ts). Used to detect + migrate
// off any stale legacy website session.
const WEBSITE_READER_UID = 'web-construct-app-reader';

// Secondary Firebase app — web-construct-website project (read-only leads/orders feed)
// Firestore rules require request.auth != null — signInWebsiteReader() must be called on app boot.
const websiteConfig = {
  apiKey:            process.env.REACT_APP_WEBSITE_FIREBASE_API_KEY             || '',
  authDomain:        process.env.REACT_APP_WEBSITE_FIREBASE_AUTH_DOMAIN         || 'web-construct-website.firebaseapp.com',
  projectId:         process.env.REACT_APP_WEBSITE_FIREBASE_PROJECT_ID          || 'web-construct-website',
  storageBucket:     process.env.REACT_APP_WEBSITE_FIREBASE_STORAGE_BUCKET      || 'web-construct-website.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_WEBSITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId:             process.env.REACT_APP_WEBSITE_FIREBASE_APP_ID              || '',
};

export const isConfigured = !!websiteConfig.apiKey;

// Initialize eagerly only when configured — avoids auth/invalid-api-key crash
const websiteApp = isConfigured
  ? (getApps().find((a) => a.name === 'website') ?? initializeApp(websiteConfig, 'website'))
  : null;

export const websiteDb   = websiteApp ? getFirestore(websiteApp) : null;
export const websiteAuth = websiteApp ? getAuth(websiteApp) : null;

// Sign in to the website project before any Firestore listeners are attached.
//
// PREFERRED path: ask the main app's backend to mint a website custom token
// (mintWebsiteReaderToken). The backend holds the website service account
// server-side, so NO credential ships in this bundle — we only exchange the
// short-lived token here via signInWithCustomToken.
//
// TRANSITION fallback: until that function is deployed (needs the WEBSITE_SA_KEY
// secret — see functions/src/functions/callable/website-token.ts), fall back to
// the legacy REACT_APP_WEBSITE_READER_* email+password so the leads feed keeps
// working. Once the minter is live, REMOVE those .env vars + this fallback and
// delete the old reader account (its password is already public in old bundles).
export async function signInWebsiteReader(): Promise<void> {
  if (!websiteAuth) {
    console.warn('[WebsiteFirebase] Website Firebase not configured — website leads feature disabled.');
    return;
  }
  // Already signed in AS THE TOKEN IDENTITY → nothing to do. If a stale/legacy
  // session is present (e.g. the old crm-reader from a previous bundle), sign it
  // out and re-auth via the minted token below, so the app actively migrates off
  // the legacy reader (lets us retire crm-reader without breaking the feed).
  if (websiteAuth.currentUser) {
    if (websiteAuth.currentUser.uid === WEBSITE_READER_UID) return;
    try { await signOut(websiteAuth); } catch { /* ignore — fall through to re-auth */ }
  }

  // Preferred: minted custom token (no credential in the bundle).
  try {
    const fns = getFunctions(mainApp, 'us-central1');
    const mint = httpsCallable<unknown, { token?: string }>(fns, 'mintWebsiteReaderToken');
    const { data } = await mint({});
    if (data?.token) {
      await signInWithCustomToken(websiteAuth, data.token);
      return;
    }
  } catch (err) {
    // Not cut over yet (function undeployed / WEBSITE_SA_KEY unset). Fall through
    // to the legacy creds below if still present.
    console.warn('[WebsiteFirebase] custom-token mint unavailable; trying legacy reader creds.', err);
  }

  const email = process.env.REACT_APP_WEBSITE_READER_EMAIL;
  const pass  = process.env.REACT_APP_WEBSITE_READER_PASS;
  if (!email || !pass) {
    console.warn('[WebsiteFirebase] No website auth available (no minted token, no reader creds) — leads feed disabled.');
    return;
  }
  await signInWithEmailAndPassword(websiteAuth, email, pass);
}
